/**
 * CorrodSkillEffect — Poison tree: cone toxic-vapor spray.
 *
 * On activation, sprays a poison_cloud cone zone in the player's current
 * facing direction. The cloud persists for 6 seconds (base), applying
 * `poison` DoT to every enemy inside at 1000ms intervals.
 *
 * Zone geometry: cone, 2 tiles deep (base), 60° arc.
 * Intensity: (3 + Potent Compound via 'taint') × Toxicologist multiplier.
 * Duration: 6000ms + Settled bonus via 'corrode:cloudDurationMs'.
 * Length: 2 tiles + Pressurized bonus via 'corrode:coneRangeTiles'.
 *
 * Cooldown tier: medium (1200ms).
 *
 * Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see poison-spec.md § Corrode
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { ZoneConfig } from '../../../systems/ZoneSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface CorrodeDeps {
  readonly zoneSystem: {
    create(config: ZoneConfig): string;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getTreePassiveEffect(
      treeName: string,
      key: string,
      aggregation: 'sum' | 'product' | 'max' | 'min',
    ): number;
  };
  readonly player: {
    readonly x: number;
    readonly y: number;
    readonly facingAngle: number; // radians
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface CorredeConfig {
  readonly cooldownMs: number;       // medium tier = 1200ms
  readonly baseLengthTiles: number;  // base cone length = 2
  readonly angleRange: number;       // full arc in degrees = 60
  readonly baseDurationMs: number;   // base cloud lifetime = 6000ms
  readonly baseIntensity: number;    // base DoT intensity = 3
}

const DEFAULT_CONFIG: CorredeConfig = {
  cooldownMs: 1200,
  baseLengthTiles: 2,
  angleRange: 60,
  baseDurationMs: 6000,
  baseIntensity: 3,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class CorrodeSkillEffect implements SkillEffect {
  readonly skillId = 'corrode';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: CorrodeDeps;
  private readonly config: CorredeConfig;

  private _cooldownRemaining = 0;
  private _activationsCount = 0;
  /** IDs of zones created by this skill instance. */
  private _zoneIds: string[] = [];

  constructor(deps: CorrodeDeps, config: Partial<CorredeConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activationsCount(): number { return this._activationsCount; }
  get zoneIds(): readonly string[] { return this._zoneIds; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    const lengthTiles = this.config.baseLengthTiles +
      this.deps.resolver.getAdditiveEffect('corrode', 'coneRangeTiles');

    const durationMs = this.config.baseDurationMs +
      this.deps.resolver.getAdditiveEffect('corrode', 'cloudDurationMs');

    const intensity = this.resolveIntensity();

    const zoneId = `corrode_cloud_${this._activationsCount}`;

    this.deps.zoneSystem.create({
      id: zoneId,
      type: 'poison_cloud',
      position: { x: this.deps.player.x, y: this.deps.player.y },
      durationMs,
      sourceSkillId: 'corrode',
      shape: 'cone',
      lengthTiles,
      angleRange: this.config.angleRange,
      facingAngle: this.deps.player.facingAngle,
      dotDefId: 'poison',
      dotIntensity: intensity,
      tickIntervalMs: 1000,
    });

    this._zoneIds.push(zoneId);
    this._cooldownRemaining = this.config.cooldownMs;
    this._activationsCount++;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private resolveIntensity(): number {
    const base = this.config.baseIntensity;
    // Inherit Potent Compound from the shared trunk (scope: 'taint')
    const bonus = this.deps.resolver.getAdditiveEffect('taint', 'dotDamagePerTick');
    const multiplier = this.deps.resolver.getTreePassiveEffect(
      'poison', 'dotDamageMultiplier', 'product',
    );
    return (base + bonus) * multiplier;
  }
}
